import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { ITherapistProgram, TherapistProgramRequest } from "../library/models/therapistProgram";
import { BaseService } from "./baseService";

/**
 * The days and hours one therapist is available.
 *
 * The read is open to anyone signed in to the clinic, because a patient choosing an hour needs
 * to know which days are even offered -- it is the only thing that tells "does not work
 * Fridays" apart from "fully booked on Friday", both of which the slot endpoint answers with an
 * empty list. The writes are the therapist's own, and the API refuses anybody else's id rather
 * than trusting a button to have been hidden.
 */
export class TherapistProgramService extends BaseService {
    /**
     * Keyed by the clinic as well as the therapist, even though the path names only the
     * therapist -- the trap `ReminderSettingsService.getSettings` documents, and it bites here
     * too: a therapist id is a *user* id, one person can practise at two clinics, and their
     * hours are a fact about a clinic rather than about them. Without the tenant in the key,
     * somebody moving between the two in a single page load is served the first one's week.
     *
     * Hand-written rather than built with createGetQuery, whose key is the URL and its query
     * parameters, because the tenant is neither.
     */
    getProgram(therapistId: number, tenantId: number | null): UseQueryOptions<ITherapistProgram, Error> {
        return {
            queryKey: ['/therapists', therapistId, 'program', tenantId],
            queryFn: (): Promise<ITherapistProgram> =>
                this.get<ITherapistProgram>(`/therapists/${therapistId}/program`),
        };
    }

    /**
     * Sends all seven days: this is a full replace, not a patch, for the reason
     * `ClinicProgramService.saveProgramMutation` gives -- "Tuesday was not mentioned" is not a
     * state the backend has an answer for.
     *
     * Resolves with what is in force rather than with what was sent, which is not always the
     * same week: the response is the clinic's hours narrowed by these.
     */
    saveProgramMutation(therapistId: number): UseMutationOptions<ITherapistProgram, Error, TherapistProgramRequest> {
        return {
            mutationFn: (payload: TherapistProgramRequest): Promise<ITherapistProgram> =>
                this.put<ITherapistProgram>(`/therapists/${therapistId}/program`, payload),
        };
    }

    /**
     * Back to the clinic's hours.
     *
     * A DELETE of the therapist's own rows rather than a PUT of a week that happens to match
     * the clinic's: a copy of today's hours would stop following them tomorrow, which is the
     * one thing pressing this asks for. Takes no variables, which `createMutation` has no shape
     * for -- and would send as a query parameter if it did.
     */
    resetProgramMutation(therapistId: number): UseMutationOptions<ITherapistProgram, Error, void> {
        return {
            mutationFn: (): Promise<ITherapistProgram> =>
                this.delete<ITherapistProgram>(`/therapists/${therapistId}/program`),
        };
    }
}
